import type { BasicGuardrailStyle } from './blissGuardrails'
import type { BlissRoomFinish } from './blissRoomFinish'
/** Saved room plan from design.blissbunkbeds.com — shared across Basic and Custom funnels. */
import type { BlissCustomDiscovery } from './blissCustomDiscovery'
import type { StudioInspiration } from './blissDesignStudio'

export type BlissRoomPlanContact = {
  name?: string
  email?: string
  phone?: string
  zip?: string
}

export type BlissRoomPlanMeasurements = {
  lengthFt?: number
  widthFt?: number
  ceilingHeightFt?: number
}

export type BlissRoomOpening = {
  id: string
  kind: 'door' | 'window'
  wall?: 'north' | 'south' | 'east' | 'west' | string
  widthIn?: number
  heightIn?: number
  offsetFromCornerIn?: number
}

export type BlissRoomPlanRoom = {
  measurements?: BlissRoomPlanMeasurements
  doors?: BlissRoomOpening[]
  windows?: BlissRoomOpening[]
}

export type BlissRoomPlanLayout = {
  finish?: BlissRoomFinish
  guardrailStyle?: BasicGuardrailStyle
  /** Private inspiration reference; never a generated measurement or production SKU. */
  inspiration?: StudioInspiration
  bunkModelId?: string
  bunkModelLabel?: string
  stairPlacement?: string
  bunkPlacement?: string
  layoutNotes?: string
  /** Full RoomBuilder snapshot (version 1, inches). Prefer this for round-trip geometry. */
  configuratorSnapshot?: unknown
  /** Every bunk room on this one quote, in order. The first room also fills the fields above. */
  rooms?: BlissPlanRoom[]
}

export type BlissPlanRoom = {
  id: string
  name: string
  configuratorSnapshot?: unknown
}

export type BlissPremiumPreferences = {
  customDiscovery?: BlissCustomDiscovery
  customStaircase?: boolean
  towerPosts?: boolean
  ledLighting?: boolean
  drawersCubbies?: boolean
  notes?: string
}

export type BlissRoomPlanAttribution = {
  /** Link back to the contact-gate lead without putting personal details in the URL. */
  quoteRequestId?: string
  source?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
}

export type BlissRoomPlanPayload = {
  contact?: BlissRoomPlanContact
  room?: BlissRoomPlanRoom
  layout?: BlissRoomPlanLayout
  premiumPreferences?: BlissPremiumPreferences
  attribution?: BlissRoomPlanAttribution
}

export type BlissRoomPlanRecord = BlissRoomPlanPayload & {
  id: string
  createdAt: string
  updatedAt: string
}

export const BLISS_ROOM_PLAN_SESSION_KEY = 'bliss_room_plan_id'

export const BLISS_DESIGN_HANDOFF_SOURCE = 'design'

export const BLISS_ROOM_PLAN_CONTEXT_SESSION_KEY = 'bliss_room_plan_context'

export function readStoredBlissRoomPlanContext(): Record<string, unknown> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(BLISS_ROOM_PLAN_CONTEXT_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    /* ignore */
  }
  return null
}
